import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Lock, AlertTriangle } from "lucide-react";
import { AnonymizedCard } from "@/components/AnonymizedCard";
import api from "@/lib/api";

// Public share-link preview. Uses the same AnonymizedCard the /how-it-works
// page + the Talent Passport preview modal use so the shape is guaranteed
// consistent across every surface.
export default function TalentPreviewPublic() {
    const { token } = useParams();
    const [state, setState] = useState({ phase: "loading", data: null, error: "" });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/public/talent-preview/${token}`);
                if (!cancelled) setState({ phase: "ready", data, error: "" });
            } catch (err) {
                if (cancelled) return;
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail || "Link not found.";
                setState({
                    phase: status === 410 ? "expired" : "notfound",
                    data: null,
                    error: detail,
                });
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    return (
        <div data-testid="talent-preview-public" className="max-w-lg mx-auto px-4 py-14">
            <div className="text-center mb-8">
                <Link to="/" className="inline-block">
                    <p className="overline text-emerald-600 dark:text-emerald-400">Simonara Talent Passport</p>
                </Link>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl">Shared Talent Preview</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Anonymized card — a candidate shared this with you. Full identity is unlocked only after mutual consent through Simonara.
                </p>
            </div>

            {state.phase === "loading" && (
                <div className="py-12 flex justify-center">
                    <Loader2 size={22} className="animate-spin text-violet-500" />
                </div>
            )}

            {state.phase === "notfound" && (
                <div
                    data-testid="talent-preview-notfound"
                    className="card-soft border-rose-500/40 bg-rose-500/[0.05] p-6 text-center"
                >
                    <AlertTriangle size={20} className="mx-auto text-rose-600" />
                    <h2 className="mt-2 font-heading font-semibold">Link not found</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {state.error || "The talent may have revoked or replaced this share link."}
                    </p>
                </div>
            )}

            {state.phase === "expired" && (
                <div
                    data-testid="talent-preview-expired"
                    className="card-soft border-amber-500/40 bg-amber-500/[0.05] p-6 text-center"
                >
                    <AlertTriangle size={20} className="mx-auto text-amber-600" />
                    <h2 className="mt-2 font-heading font-semibold">Link expired</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Ask the talent to send a fresh link — Simonara share links expire after 30 days.
                    </p>
                </div>
            )}

            {state.phase === "ready" && state.data?.card && (
                <>
                    <AnonymizedCard card={state.data.card} testId="talent-preview-card" />
                    <p className="text-[11px] text-muted-foreground mt-3 text-center">
                        <Lock size={11} className="inline mb-0.5" /> No name, email, or phone shown.
                        {" "}
                        <Link to="/how-it-works" className="underline hover:text-foreground">How this works →</Link>
                    </p>
                    <div className="mt-6 text-center">
                        <Link
                            to="/"
                            data-testid="talent-preview-signup-cta"
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold"
                        >
                            Hire on Simonara
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
}
